import React, { useEffect, useState } from 'react';
import { publicApi } from '../api/client';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Message } from '../utils/chatStorage';
import { track } from '../utils/analytics';

interface SharedMessage {
  role: string;
  content: string;
  moduleDisplayName?: string;
}

interface SharedChatData {
  share_id: string;
  module_type: string | null;
  title: string | null;
  messages: SharedMessage[];
  ticket_raisable: boolean;
  created_at: string;
}

interface SharedChatViewProps {
  shareId: string;
  onContinueInApp: (messages: Message[], module: string | undefined) => void;
}

const MODULE_DISPLAY: Record<string, string> = {
  resident: 'Resident App',
  admin: 'Admin Dashboard',
  smart_devices: 'Smart Devices',
};

const SharedChatView: React.FC<SharedChatViewProps> = ({ shareId, onContinueInApp }) => {
  const [data, setData] = useState<SharedChatData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi
      .get<SharedChatData>(`/api/share/${shareId}`, { timeout: 10000 })
      .then((res) => {
        setData(res.data);
        track('share_viewed', { module: res.data.module_type ?? 'unknown' });
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setError('This share link has expired or does not exist.');
        } else {
          setError('Failed to load shared chat. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [shareId]);

  const handleContinue = () => {
    if (!data) return;
    const messages: Message[] = data.messages.map((m) => ({
      role: m.role,
      content: m.content,
      moduleDisplayName: m.moduleDisplayName,
    }));
    onContinueInApp(messages, data.module_type ?? undefined);
  };

  const moduleLabel = data?.module_type ? MODULE_DISPLAY[data.module_type] ?? data.module_type : null;
  const moduleClass = data?.module_type
    ? { resident: 'module-tab-resident', admin: 'module-tab-admin', smart_devices: 'module-tab-smart' }[data.module_type] ?? ''
    : '';

  return (
    <div className="chat-window">
      {/* Tab bar — read-only, shows which module this chat belongs to */}
      <div className="module-tabs">
        <span className={`module-tab active ${moduleClass}`} style={{ cursor: 'default' }}>
          {moduleLabel ?? 'Shared Chat'}
        </span>
        <span style={{
          marginLeft: 'auto',
          padding: '0 14px',
          fontSize: '0.75rem',
          color: '#9ca3af',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          flexShrink: 0,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          Shared chat
        </span>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
          <div className="typing-indicator"><span /><span /><span /></div>
          <p style={{ fontSize: '0.8125rem', color: '#9ca3af', margin: 0 }}>Loading shared chat…</p>
        </div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center', padding: '0 24px' }}>{error}</p>
        </div>
      ) : data && (
        <>
          <div className="messages-container">
            {/* Shared-by banner — sits above the first message */}
            <div style={{
              width: 'min(85%, 1280px)',
              margin: '0 auto 20px',
              padding: '10px 14px',
              background: '#f3f4f6',
              borderRadius: '8px',
              fontSize: '0.8rem',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              This conversation was between <strong style={{ color: '#374151' }}>MIRA One</strong> and <strong style={{ color: '#374151' }}>Anonymous User</strong>
            </div>

            {data.messages.map((msg, i) => (
              <div key={i} className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}>
                <div className="message-header">
                  <span className="message-role">{msg.role === 'user' ? 'Anonymous User' : 'MIRA'}</span>
                  {msg.role === 'assistant' && msg.moduleDisplayName && (
                    <span className={`module-tag module-tag-${msg.moduleDisplayName.toLowerCase().replace(/\s+/g, '-')}`}>
                      {msg.moduleDisplayName}
                    </span>
                  )}
                </div>
                <div className="message-content">
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Continue CTA — hidden when ticket was raised or bot prompted escalation */}
          <div className="input-container">
            {data.ticket_raisable ? (
              <div style={{
                width: 'min(85%, 1280px)',
                margin: '0 auto',
                padding: '12px 16px',
                background: '#f3f4f6',
                border: '1.5px solid #e5e7eb',
                borderRadius: '12px',
                textAlign: 'center',
              }}>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#9ca3af' }}>
                  This conversation has been escalated to support.
                </p>
              </div>
            ) : (
              <div style={{
                width: 'min(85%, 1280px)',
                margin: '0 auto',
                padding: '12px 16px',
                background: '#fffbeb',
                border: '1.5px solid #fcd34d',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
              }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151', lineHeight: 1.5 }}>
                  Continue this conversation or ask your own question
                </p>
                <button
                  onClick={handleContinue}
                  style={{
                    flexShrink: 0,
                    padding: '9px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'var(--mg-yellow)',
                    color: '#111827',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Continue this chat
                </button>
              </div>
            )}
            <div className="input-footer-disclaimer">
              MIRA is an AI Chatbot and can make mistakes. Please verify important information.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SharedChatView;
